import { useEffect } from "react";

import { processResourcePermissions } from "@/components/pieces/roleplays/sharing/permissionTransformers";
import {
  ResourceType,
  useResourcePermissionsLazyQuery,
  VisibilityScope,
} from "@/graphql";
import { ProcessedPermissions } from "@/types/permissions";

interface UseResourcePermissionsProps<T> {
  resource: T;
  resourceType: ResourceType;
  isOpen: boolean;
  onPermissionsLoaded: (
    permissions: ProcessedPermissions & {
      visibilityScope: VisibilityScope;
      autoShareSessions?: boolean;
    },
  ) => void;
}

/**
 * Generic hook to manage resource permissions fetching and processing
 */
export function useResourcePermissions<
  T extends {
    id: string;
    visibilityScope?: VisibilityScope | null;
    autoShareSessions?: boolean | null;
  },
>({
  resource,
  resourceType,
  isOpen,
  onPermissionsLoaded,
}: UseResourcePermissionsProps<T>) {
  const [fetchResourcePermissions, { loading, error }] =
    useResourcePermissionsLazyQuery({
      fetchPolicy: "network-only",
      onCompleted: (response) => {
        if (response?.resourcePermissions) {
          const processed = processResourcePermissions(
            response.resourcePermissions,
          );

          onPermissionsLoaded({
            ...processed,
            visibilityScope: resource.visibilityScope || VisibilityScope.User,
            autoShareSessions: resource.autoShareSessions || false,
          });
        }
      },
    });

  // Fetch permissions when modal opens
  useEffect(() => {
    if (isOpen && resource?.id) {
      fetchResourcePermissions({
        variables: {
          resourceType,
          resourceId: resource.id,
        },
      });
    }
  }, [isOpen, resource?.id, resourceType, fetchResourcePermissions]);

  // Refetch function for use after mutations
  const refetchPermissions = () => {
    if (resource?.id) {
      return fetchResourcePermissions({
        variables: {
          resourceType,
          resourceId: resource.id,
        },
      });
    }
  };

  return {
    loading,
    error,
    refetchPermissions,
  };
}
